"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";

import type { AppIcon } from "@/components/appIcons";
import { SearchableSelectPortals } from "@/components/ui/searchable-select-portals";
import { cn } from "@/lib/utils";

interface SearchableSelectSurfaceRenderArgs {
  open: boolean;
  setOpen: (open: boolean) => void;
  focusInput: () => void;
  desktopInputRef: RefObject<HTMLInputElement | null>;
}

interface SearchableSelectSurfaceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  icon?: AppIcon;
  shouldFocus?: boolean;
  selectedIndex?: number;
  renderDesktopInputInPanel?: boolean;
  renderMobileInputInPanel?: boolean;
  containerClassName?: string;
  desktopPanelClassName?: string;
  desktopOptionsClassName: string;
  mobilePanelClassName?: string;
  mobileOptionsClassName?: string;
  onClose?: () => void;
  renderTrigger: (args: SearchableSelectSurfaceRenderArgs) => ReactNode;
  renderInput?: (ref: RefObject<HTMLInputElement | null>, mode: "desktop" | "mobile") => ReactNode;
  renderOptions: (
    ref: RefObject<HTMLUListElement | null>,
    className: string,
    mode: "desktop" | "mobile",
  ) => ReactNode;
}

type DesktopPanelStyle = CSSProperties & Record<"--select-trigger-width", string>;

export function SearchableSelectSurface({
  open,
  onOpenChange,
  title,
  icon,
  shouldFocus,
  selectedIndex = -1,
  renderDesktopInputInPanel = true,
  renderMobileInputInPanel = true,
  containerClassName,
  desktopPanelClassName,
  desktopOptionsClassName,
  mobilePanelClassName,
  mobileOptionsClassName = "max-h-[calc(min(70svh,32rem)_-_7rem)] overflow-y-auto",
  onClose,
  renderTrigger,
  renderInput,
  renderOptions,
}: SearchableSelectSurfaceProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const desktopPanelRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const desktopOptionsRef = useRef<HTMLUListElement>(null);
  const mobileOptionsRef = useRef<HTMLUListElement>(null);
  const [desktopPanelStyle, setDesktopPanelStyle] = useState<DesktopPanelStyle | null>(null);

  const close = useCallback(() => {
    onOpenChange(false);
    onClose?.();
  }, [onClose, onOpenChange]);

  const focusInput = useCallback(() => {
    const input = [mobileInputRef.current, desktopInputRef.current].find(
      (el) => el && el.getClientRects().length > 0,
    );
    input?.focus({ preventScroll: true });
  }, []);

  const updateDesktopPanelPosition = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger) return;

    const viewportPadding = 8;
    const gap = 4;
    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = desktopPanelRef.current?.getBoundingClientRect();
    const panelWidth = panelRect?.width || triggerRect.width;
    const panelHeight = panelRect?.height || 0;
    const left = Math.max(
      viewportPadding,
      Math.min(triggerRect.left, window.innerWidth - viewportPadding - panelWidth),
    );
    const spaceBelow = window.innerHeight - triggerRect.bottom - gap - viewportPadding;
    const spaceAbove = triggerRect.top - gap - viewportPadding;
    const openAbove = panelHeight > spaceBelow && spaceAbove > spaceBelow;
    const preferredTop = openAbove ? triggerRect.top - gap - panelHeight : triggerRect.bottom + gap;
    const maxTop =
      panelHeight > 0 ? window.innerHeight - viewportPadding - panelHeight : preferredTop;
    const top = Math.max(viewportPadding, Math.min(preferredTop, maxTop));

    setDesktopPanelStyle({
      "--select-trigger-width": `${triggerRect.width}px`,
      left,
      maxHeight: `calc(100vh - ${viewportPadding * 2}px)`,
      maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
      minWidth: triggerRect.width,
      top,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setDesktopPanelStyle(null);
      return;
    }

    updateDesktopPanelPosition();
    const animationFrame = window.requestAnimationFrame(updateDesktopPanelPosition);

    const resizeObserver =
      typeof ResizeObserver !== "undefined" && desktopPanelRef.current
        ? new ResizeObserver(updateDesktopPanelPosition)
        : null;
    resizeObserver?.observe(desktopPanelRef.current as Element);

    window.addEventListener("resize", updateDesktopPanelPosition);
    window.addEventListener("scroll", updateDesktopPanelPosition, true);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateDesktopPanelPosition);
      window.removeEventListener("scroll", updateDesktopPanelPosition, true);
    };
  }, [open, updateDesktopPanelPosition]);

  useEffect(() => {
    if (!open) return;

    if (shouldFocus) {
      requestAnimationFrame(focusInput);
      window.setTimeout(focusInput, 80);
    }

    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !desktopPanelRef.current?.contains(target) &&
        !modalRef.current?.contains(target)
      ) {
        close();
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [close, focusInput, open, shouldFocus]);

  useEffect(() => {
    if (!open || selectedIndex < 0) return;

    const list = [mobileOptionsRef.current, desktopOptionsRef.current].find(
      (el) => el && el.getClientRects().length > 0,
    );
    const item = list?.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [open, selectedIndex]);

  return (
    <div ref={containerRef} className={cn("relative", containerClassName)}>
      {renderTrigger({
        open,
        setOpen: onOpenChange,
        focusInput,
        desktopInputRef,
      })}

      {open ? (
        <SearchableSelectPortals
          title={title}
          icon={icon}
          desktopPanelRef={desktopPanelRef}
          modalRef={modalRef}
          desktopInputRef={desktopInputRef}
          mobileInputRef={mobileInputRef}
          desktopOptionsRef={desktopOptionsRef}
          mobileOptionsRef={mobileOptionsRef}
          desktopPanelStyle={desktopPanelStyle}
          desktopPanelClassName={desktopPanelClassName}
          desktopOptionsClassName={desktopOptionsClassName}
          mobilePanelClassName={mobilePanelClassName}
          mobileOptionsClassName={mobileOptionsClassName}
          renderDesktopInputInPanel={renderDesktopInputInPanel}
          renderMobileInputInPanel={renderMobileInputInPanel}
          close={close}
          renderInput={renderInput}
          renderOptions={renderOptions}
        />
      ) : null}
    </div>
  );
}
